// Layer 1 (Data Schema): Paul's V8 Met Diagnostics register — the ten named
// symptom sections and sixty numbered root-cause rows (MD-001–MD-060) his
// workbook actually carries (docs/meetings/inputs/2026-09-09-paul-fmea-v8.md
// § Met Diagnostics register). Shape only — the 60-row data lives in
// `src/versions/v1/shared/bunker-hill-met-diagnostics-register.ts`.
//
// This is the yardstick the rendered rule set (#270) measures itself
// against: the mock carries a structured `DiagnosticRule` for a fraction of
// these causes. Each cause is one of three states — `covered` (a live rule
// names it), `proposed` (a not-yet-approved rule names it), `gap` (neither)
// — and the honest figure is `MET_DIAGNOSTICS_CAUSES` counted against the
// seeded rules per state, never typed.

/** The ten `▶` section headers, verbatim and in Paul's own order. */
export type MetDiagnosticsSection =
  | 'Pb Recovery Below Target'
  | 'Pb Con Grade Below 67% Pb'
  | 'Ag Recovery Below Target'
  | 'Zn Recovery Below Target'
  | 'Zn Con Grade Below Spec'
  | 'Both Pb + Zn Recovery Declining'
  | 'Tailings Grades Elevated'
  | 'Additional Grinding Circuit Root Causes'
  | 'Additional Flotation Circuit Root Causes'
  | 'Concentrate Penalty Elements / Impurities';

/** Affects-flag columns M–R, Paul's own codes decoded. */
export type AffectsFlag =
  | 'pb-recovery'
  | 'zn-recovery'
  | 'ag-recovery'
  | 'pb-con-grade'
  | 'zn-con-grade'
  | 'penalty-elements';

export const AFFECTS_FLAG_CODE: Record<AffectsFlag, string> = {
  'pb-recovery': 'PbR',
  'zn-recovery': 'ZnR',
  'ag-recovery': 'AgR',
  'pb-con-grade': 'PbG',
  'zn-con-grade': 'ZnG',
  'penalty-elements': 'Pen',
};

/**
 * Paul's own 1–5 scale, ingested verbatim from the sheet's Likelihood and
 * Priority columns (paul-fmea.A4, #271). The sheet gives neither column a
 * worded anchor — the 1–10 Severity/Occurrence/Detection legend on line 72
 * of the source doc belongs to the equipment sheets' criticality matrix, a
 * different instrument on different rows, and borrowing its wording here
 * would invent a legend Paul never wrote. Render the bare number; order by
 * it; never attach a word like "critical" or "minor".
 *
 * Direction, read from the rows themselves (never assumed): lower Priority
 * = more urgent / investigate first — MD-001 (coarse grind, a common,
 * readily-diagnosed cause) is Priority 1; MD-010 (oxidised lead minerals, a
 * rarer, harder-to-confirm cause) is Priority 4. The two columns correlate
 * strongly but not deterministically across the register (r = −0.79; e.g.
 * MD-030 is Likelihood 5 / Priority 1 and MD-052 is Likelihood 1 / Priority
 * 5, but Likelihood-3 rows carry Priority 1, 2 and 3, and Likelihood-2 rows
 * carry Priority 2, 3 and 4) — Priority reads as "check this first," not as
 * a severity score: a more likely cause carries a lower Priority number.
 */
export type MetDiagnosticsLikelihood = 1 | 2 | 3 | 4 | 5;
export type MetDiagnosticsPriority = 1 | 2 | 3 | 4 | 5;

/** One MD-### row of the Met Diagnostics register, as Paul's sheet carries it. */
export type MetDiagnosticsCause = {
  /** `MD-001` … `MD-060`. */
  id: string;
  /** The section the row physically sits under on the sheet (column-quirk
   * rows keep the section they were filed under, never silently re-filed —
   * see `affects`, which is the authoritative per-row signal). */
  section: MetDiagnosticsSection;
  /** Possible root cause, column C, verbatim. */
  cause: string;
  /** Column D — Paul's own category label (not the mock's `CauseCategory`). */
  causeCategory: string;
  /** Column M–R — authoritative regardless of `section`. */
  affects: readonly AffectsFlag[];
  /** Column — how often Paul sees this cause in practice, 1 (rare) to 5
   * (frequent). Ingested, belongs to the cause. Never confused with
   * `DiagnosticRule.priority` (`RulePriority`, `P1`–`P4`), which is authored,
   * belongs to a rule and an alarm, and drives the deck's HIGH/MED/LOW
   * severity display — a different quantity that happens to share a word. */
  likelihood: MetDiagnosticsLikelihood;
  /** Column — Paul's own investigate-first ranking, 1 (first) to 5 (last).
   * Ingested, belongs to the cause. See `MetDiagnosticsLikelihood` above for
   * the RulePriority collision this must never read as. */
  priority: MetDiagnosticsPriority;
};

/** Section order + the sheet's own stated target, for the honest header line. */
export const MET_DIAGNOSTICS_SECTION_TARGET: Record<MetDiagnosticsSection, string> = {
  'Pb Recovery Below Target': 'Pb recovery > 85%',
  'Pb Con Grade Below 67% Pb': 'Pb con grade > 67% Pb',
  'Ag Recovery Below Target': 'Ag recovery > 75%',
  'Zn Recovery Below Target': 'Zn recovery > 85%',
  'Zn Con Grade Below Spec': 'Zn con grade > 55% Zn',
  'Both Pb + Zn Recovery Declining': 'reuses the Pb and Zn recovery pair',
  'Tailings Grades Elevated': 'tailings Pb < 0.3%, Zn < 0.5%',
  'Additional Grinding Circuit Root Causes': '—',
  'Additional Flotation Circuit Root Causes': '—',
  'Concentrate Penalty Elements / Impurities': 'smelter contract penalty thresholds',
};

export const MET_DIAGNOSTICS_SECTION_ORDER: readonly MetDiagnosticsSection[] = [
  'Pb Recovery Below Target',
  'Pb Con Grade Below 67% Pb',
  'Ag Recovery Below Target',
  'Zn Recovery Below Target',
  'Zn Con Grade Below Spec',
  'Both Pb + Zn Recovery Declining',
  'Tailings Grades Elevated',
  'Additional Grinding Circuit Root Causes',
  'Additional Flotation Circuit Root Causes',
  'Concentrate Penalty Elements / Impurities',
];
